// Photos in the support chat (betup-server-api docs/support-images.md). A photo is a whole line of the message text,
// ```{"type":"IMAGE","url":"https://betup.org/uploads_api/support/12_….jpg"}``` — the share-block syntax of the app.
// Only our own uploads are drawn as pictures: a player can type any block, and an image from anywhere else would make
// the agent's browser fetch it. Anything else stays text, so the agent still sees what was sent.

export const MAX_SUPPORT_PHOTOS = 5;

/** Longest side of a photo staff attach; the server takes up to 2048. */
export const SUPPORT_PHOTO_MAX_SIDE = 1600;

/** The main API's cap for a support photo (2.5 MiB, like the app); analytics and the api accept 3 MB a file. */
export const SUPPORT_PHOTO_MAX_BYTES = 2_621_440;

/** Hosts the main API serves uploads from (production and the test stack). */
export const SUPPORT_PHOTO_HOSTS = ['betup.org', 'www.betup.org', 'qbetapp.info', 'www.qbetapp.info'];

// {playerId}_{uuid} for a player's photo, staff_{uuid} for one sent from here.
const PHOTO_PATH = /^\/uploads_api\/support\/(\d+|staff)_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.(jpg|png|webp)$/;
const BLOCK = /^```\s*(\{.*\})\s*```$/;

export const isSupportPhotoUrl = (url: string): boolean => {
    try {
        const parsed = new URL(url);
        return parsed.protocol === 'https:'
            && parsed.port === ''
            && parsed.search === ''
            && parsed.hash === ''
            && SUPPORT_PHOTO_HOSTS.includes(parsed.hostname.toLowerCase())
            && PHOTO_PATH.test(parsed.pathname);
    } catch {
        return false;
    }
}

/** The url of an IMAGE line, or null for any other line. */
const imageUrlOf = (line: string): string | null => {
    const match = BLOCK.exec(line.trim());
    if (!match) return null;
    try {
        const json = JSON.parse(match[1]);
        if (json == null || typeof json !== 'object') return null;
        if (String(json.type ?? '').toUpperCase() !== 'IMAGE') return null;
        const url = String(json.url ?? '').trim();
        return url === '' ? null : url;
    } catch {
        return null;
    }
}

export interface SupportMessageParts {
    /** The text without the photo lines drawn as pictures. */
    words: string
    /** Our uploads, in order. */
    photos: string[]
}

/** Splits a message into its words and the photos to draw; a block with a foreign url stays in the words. */
export const parseSupportMessage = (text: string | null | undefined): SupportMessageParts => {
    if (!text) return { words: '', photos: [] };
    if (!text.includes('```')) return { words: text, photos: [] };
    const kept: string[] = [];
    const photos: string[] = [];
    for (const line of text.replace(/\r\n/g, '\n').split('\n')) {
        const url = imageUrlOf(line);
        if (url != null && isSupportPhotoUrl(url)) {
            photos.push(url);
        } else {
            kept.push(line);
        }
    }
    return { words: kept.join('\n').trim(), photos };
}

/** The message for [words] and [urls]: the words, then one line per photo (what the app and messaging read). */
export const composeSupportMessage = (words: string, urls: string[]): string => [
    ...(words.trim() === '' ? [] : [words.trim()]),
    ...urls.map(url => '```' + JSON.stringify({ type: 'IMAGE', url }) + '```'),
].join('\n');

/**
 * [file] as a JPEG of at most SUPPORT_PHOTO_MAX_SIDE a side and SUPPORT_PHOTO_MAX_BYTES (transparency on white),
 * stepping the quality down and then the size; null when the browser can't read it as an image.
 */
export const prepareSupportPhoto = async (file: Blob): Promise<Blob | null> => {
    let bitmap: ImageBitmap;
    try {
        bitmap = await createImageBitmap(file);
    } catch {
        return null;
    }
    try {
        let scale = Math.min(1, SUPPORT_PHOTO_MAX_SIDE / Math.max(bitmap.width, bitmap.height));
        for (let shrink = 0; shrink < 5; shrink++) {
            const width = Math.max(1, Math.round(bitmap.width * scale));
            const height = Math.max(1, Math.round(bitmap.height * scale));
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const context = canvas.getContext('2d');
            if (!context) return null;
            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, width, height);
            context.drawImage(bitmap, 0, 0, width, height);
            for (const quality of [0.9, 0.82, 0.74, 0.66]) {
                const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));
                if (blob && blob.size <= SUPPORT_PHOTO_MAX_BYTES) return blob;
            }
            scale *= 0.8;
        }
        return null;
    } finally {
        bitmap.close();
    }
}

export interface SupportMessagePreview {
    /** The words, on one line. */
    words: string
    /** Photo lines (any url: nothing is loaded for a preview). */
    photos: number
    /** Other shared blocks by type, lower-case (match, post, team, channel …). */
    shared: string[]
}

// What messaging writes instead of the blocks in the lists it serves to the dashboard (SharedMessagePreview, English).
const SERVER_SENTENCES: Array<[RegExp, (preview: SupportMessagePreview) => void]> = [
    [/^Sent a photo(?::\s*|$)/, p => { p.photos += 1; }],
    [/^Shared an? (post|match|team|channel)(?::\s*|$)/, () => undefined],
];

/**
 * A support message for a one-line list cell: widget blocks (```{"type":…}``` lines) come out of the words and are
 * counted, so the cell can draw icons instead of JSON. Also reads messaging's own "Sent a photo: …" wording.
 */
export const previewSupportMessage = (text: string | null | undefined): SupportMessagePreview => {
    const preview: SupportMessagePreview = { words: '', photos: 0, shared: [] };
    if (!text) return preview;
    const kept: string[] = [];
    for (const line of text.replace(/\r\n/g, '\n').split('\n')) {
        const match = BLOCK.exec(line.trim());
        let type: string | null = null;
        if (match) {
            try {
                const json = JSON.parse(match[1]);
                if (json != null && typeof json === 'object' && typeof json.type === 'string' && json.type.trim() !== '') {
                    type = json.type.trim().toLowerCase();
                }
            } catch {
                // Not JSON: it stays text.
            }
        }
        if (type === 'image') preview.photos += 1;
        else if (type != null) preview.shared.push(type);
        else kept.push(line);
    }
    let words = kept.join(' ').replace(/\s+/g, ' ').trim();
    for (const [pattern, apply] of SERVER_SENTENCES) {
        const found = pattern.exec(words);
        if (!found) continue;
        if (found[1]) preview.shared.push(found[1]);
        apply(preview);
        words = words.slice(found[0].length).trim();
        break;
    }
    preview.words = words;
    return preview;
}
